import type { RotationAlbum } from './types'
export interface ShelfItem {
  album: RotationAlbum
  index: number
  present: boolean
  delay: number
  moveDelay: number
  revision: number
  lane: number
}
export function initialShelf(albums: RotationAlbum[]): ShelfItem[] {
  return albums.map((album, index) => ({
    album,
    index,
    present: true,
    delay: index * 75,
    moveDelay: 0,
    revision: 0,
    lane: index,
  }))
}
export function changeShelf(
  current: ShelfItem[],
  albums: RotationAlbum[],
  stagger = 75,
  visibleCount = 9,
): ShelfItem[] {
  const revision = Math.max(0, ...current.map((item) => item.revision)) + 1
  const nextIds = new Set(albums.map((album) => album.key))
  const nextVisible = new Set(
    albums.slice(0, visibleCount).map((album) => album.key),
  )
  const old = new Map(current.map((item) => [item.album.key, item]))
  const vacating = current.filter(
    (item) => item.index < visibleCount && !nextVisible.has(item.album.key),
  )
  const moveDelay = vacating.length
    ? Math.max(...vacating.map((item) => item.index * stagger)) + 400
    : 0
  const moving = albums.some((album, index) => {
    const from = old.get(album.key)
    return (
      index < visibleCount &&
      from &&
      from.index < visibleCount &&
      from.index !== index
    )
  })
  const entranceDelay = moveDelay + (moving ? 600 : 0)
  const occupiedLanes = new Set(current.map((item) => item.lane))
  let freeLane = 0
  const next = albums.map((album, index) => {
    const from = old.get(album.key)
    const retained = from && from.index < visibleCount
    while (occupiedLanes.has(freeLane)) freeLane++
    const lane = from?.lane ?? freeLane
    occupiedLanes.add(lane)
    return {
      album,
      index,
      present: true,
      delay:
        index >= visibleCount
          ? (from?.index ?? index) * stagger
          : retained
            ? moveDelay
            : entranceDelay + index * stagger,
      moveDelay,
      revision,
      lane,
    }
  })
  const exits = current.flatMap((item) =>
    nextIds.has(item.album.key)
      ? []
      : [
          {
            ...item,
            present: false,
            delay: item.index < visibleCount ? item.index * stagger : 0,
            moveDelay,
            revision,
          },
        ],
  )
  return [...next, ...exits]
}
export function shelfTransitionDuration(items: ShelfItem[]) {
  return (
    Math.max(
      0,
      ...items.map((item) => Math.max(item.delay + 400, item.moveDelay + 600)),
    ) + 50
  )
}
